<template>
  <section class="tudastar-banner no-print container-fluid">
    <div class="container">
      <div class="row justify-content-center">
        <h1 class="page-section__title col-12">
          <span class="page-section__title__background">{{ t('banners.tudastar.title') }}</span>
          <span class="page-section__title__main">{{ t('banners.tudastar.description') }}</span>
        </h1>
        <div class="tudastar-banner__column tudastar-banner__column--left col-lg-6 order-0 order-md-0">
          <form class="tudastar-banner__form" novalidate @submit.prevent="sendEmail">
            <h3 class="tudastar-banner__form__title text-left">{{ t('banners.tudastar.form.title') }}</h3>
            <p class="tudastar-banner__form__email-notification">{{ t('banners.tudastar.form.emailNotification') }}</p>

            <div class="form-group">
              <label for="email">{{ t('banners.tudastar.form.fields.email') }}</label>
              <input
                v-model="validations.email.$model"
                type="email"
                id="email"
                name="email"
                :placeholder="t('banners.tudastar.form.fields.emailPlaceholder')"
                class="form-control"
                :class="{ 'is-invalid': validations.email.$error || hasServerValidationError('email') }"
              />
              <ErrorMessage :field="validations.email" />
              <ServerValidation field="email" :server-validation="serverValidation" />
            </div>

            <div class="form-group">
              <label class="custom-label">
                <input v-model="validations.privacyPolicy.$model" type="checkbox" name="privacy" />
                <span class="field-decorator"></span>
                <span v-html="t('banners.tudastar.form.fields.privacyPolicy')"></span>
              </label>
              <ErrorMessage :field="validations.privacyPolicy" />
            </div>

            <div class="form-group">
              <label class="custom-label">
                <input v-model="newsLetter" type="checkbox" name="newsletter" />
                <span class="field-decorator"></span>
                <span v-html="t('banners.tudastar.form.fields.newsLetter')"></span>
              </label>
            </div>

            <hr />

            <p class="tudastar-banner__form__postcode-notification">
              {{ t('banners.tudastar.form.postCodeNotification') }}
            </p>

            <div class="form-group">
              <label for="postCode">{{ t('banners.tudastar.form.fields.postCode') }}</label>
              <input
                v-model="validations.postCode.$model"
                type="text"
                inputmode="numeric"
                pattern="[0-9]{4}"
                maxlength="4"
                id="postCode"
                name="postCode"
                :placeholder="t('banners.tudastar.form.fields.postCodePlaceholder')"
                class="form-control"
                :class="{ 'is-invalid': validations.postCode.$error || hasServerValidationError('postCode') }"
              />
              <ErrorMessage :field="validations.postCode" />
              <ServerValidation field="postCode" :server-validation="serverValidation" />
            </div>

            <button type="submit" class="btn btn-dark" :disabled="isLoading">
              {{ t('banners.tudastar.form.submit.label') }}
            </button>
          </form>
        </div>

        <div class="tudastar-banner__column tudastar-banner__column--right col-lg-6">
          <img
            class="tudastar-banner__image__checklist"
            src="@/assets/images/tudastar/terran-tudastar.png"
            alt="tetotepitek"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent, reactive, toRefs } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

import useFormControl from '@/hooks/formControl'
import httpClient from '@/services/api/httpClient'
import useGtmEvent from '@/hooks/gtm'

import ErrorMessage from '@/components/Global/ErrorMessage.vue'
import ServerValidation from '@/components/Global/ServerValidation.vue'

export default defineComponent({
  name: 'TudastarBanner',
  components: {
    ErrorMessage,
    ServerValidation
  },
  props: {
    results: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const { serverValidation, hasServerValidationError } = useFormControl()
    const toast = useToast()
    const { trackEvent } = useGtmEvent()
    const { t } = useI18n()

    const state = reactive({
      email: '',
      postCode: '',
      privacyPolicy: false,
      newsLetter: false,
      isLoading: false
    })

    const postCode = value => {
      if (!value) return true
      return /^[1-9]\d{3}$/.test(value)
    }

    const validationRules = {
      email: {
        required,
        email
      },
      postCode: {
        postCode
      },
      privacyPolicy: {
        required: val => val === true
      }
    }

    const validations = useVuelidate(validationRules, state)

    const resetForm = () => {
      state.email = ''
      state.postCode = ''
      state.privacyPolicy = false
      state.newsLetter = false

      setTimeout(() => {
        validations.value.email.$reset()
        validations.value.postCode.$reset()
        validations.value.privacyPolicy.$reset()
        validations.value.$reset()
      }, 10)
    }

    async function sendEmail() {
      if (state.isLoading) return

      validations.value.$touch()

      if (validations.value.$invalid) return

      try {
        state.isLoading = true
        serverValidation.value = {}

        const payload = {
          email: state.email,
          postCode: state.postCode,
          privacyPolicy: state.privacyPolicy,
          newsLetter: state.newsLetter
        }

        const calculationId = props.results.calculation_id

        await httpClient.post(`send-calculation/${calculationId}`, payload, {})

        trackEvent({
          category: 'e-mail',
          action: 'kuldes'
        })

        toast.success(t('banners.tudastar.form.submit.success', { email: state.email }))
        resetForm()
      } catch (error) {
        if (error.errStatus === 404) {
          serverValidation.value = Object.assign({}, error.errData.error)
        }
        if (error.errStatus === 422) {
          serverValidation.value = Object.assign({}, error.errData.data.data.params)
        }
      } finally {
        state.isLoading = false
      }
    }

    return {
      ...toRefs(state),
      sendEmail,
      validations,
      serverValidation,
      hasServerValidationError,
      resetForm,
      t
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/assets/style/base/variables' as *;

.tudastar-banner {
  margin-bottom: 64px;
  padding: 64px 0 128px !important;
  background: linear-gradient(97.25deg, #fdab1c -3.99%, #fb9f1e 23.24%, #e42730 110.44%);

  .page-section__title__background {
    color: #ffffff;
    opacity: 0.2;

    @media screen and (max-width: 600px) {
      font-size: 40px;
    }
  }

  .page-section__title__main {
    color: #ffffff;
  }

  &__column {
    &--right {
      display: none;
    }

    @media screen and (min-width: 992px) {
      &--left {
        padding-right: 56px;
      }

      &--right {
        display: block;
        padding-left: 56px;
      }
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    color: #111;

    @media screen and (min-width: 992px) {
      align-items: flex-start;
    }

    &__title {
      font-size: 20px;
      line-height: normal;

      @media screen and (min-width: 992px) {
        font-size: 24px;
      }
    }

    .form-group {
      width: 100%;
    }

    .form-control {
      &#email {
        width: 340px;
      }

      &#postCode {
        width: 100px;
      }
    }

    .custom-label {
      display: flex;
      align-items: flex-start;
      margin-bottom: 0;

      .field-decorator:before {
        border: none;
        background-color: white;
      }

      > input {
        margin-right: 8px;
        width: 13px;
      }

      > span:last-child {
        padding-left: 10px;
      }
    }

    hr {
      margin-bottom: 1rem;
      border: 1px solid white;
      width: 100%;
    }

    &__postcode-notification {
      font-weight: 700;
    }
  }
}
</style>
